<template>
  <div ref="container">
    <MarketSwitcherBanner
      :show="showRegionBanner && showShopSwitcher"
      :is-mobile="isMobile"
      :message="regionBannerMessage"
      class="-mx-16px sm:-mx-16px lg:-mx-32px -mt-16px sm:-mt-16px lg:-mt-32px mb-16px sm:mb-32px"
      @change="switchSelectedShop"
      @dismiss="dismissShop"
    >
      <MarketSwitcher
        :shops="shop.marketRegions"
        :region="shopSelectedRegion"
        :data-qa-id="pendo('switchShop')"
        @change="(e) => changeShop(e.country)"
      />
    </MarketSwitcherBanner>

    <div
      v-if="!loading && hasMultipleCategories"
      ref="navBarContainer"
      class="flex items-center px-4 sm:px-8 sticky top-0 z-2 backdrop-blur-md -mx-16px sm:-mx-16px lg:-mx-32px -mt-16px sm:-mt-16px lg:-mt-32px mb-16px sm:mb-28px"
      :class="{ 'bg-white/70': isPageScrolledVeritcally }"
    >
      <MdlButton
        v-if="isNavBarScrollable"
        icon="chevron_left"
        :disabled="navBarScrollPosition === 'left'"
        @click="scrollNavBarRight()"
        class="-ml-4"
      />

      <div id="navBar" ref="navBar" class="flex items-baseline gap-6 overflow-x-scroll no-scrollbar">
        <a
          v-for="(category, index) in categories"
          :key="category.id"
          :ref="(el) => (navBarCategoryRefs[index] = el)"
          class="cursor-pointer whitespace-nowrap text-md sm:text-lg no-underline text-black pb-2"
          :class="{
            'p-2': index !== activeCategory,
            'font-bold border-0 border-b-[2px] border-solid border-indicator': index === activeCategory,
          }"
          :data-qa-id="pendo(`goToCategory.${category.id}'`)"
          :href="'#category-' + category.id"
          @click="activateCategory(category.id)"
        >
          {{ category.name.default }}
        </a>
      </div>

      <MdlButton
        v-if="isNavBarScrollable"
        icon="chevron_right"
        :disabled="navBarScrollPosition === 'right'"
        @click="scrollNavBarLeft()"
        class="-mr-4"
      />
    </div>

    <Toolbar
      filters
      :loading="loading"
      :submit="submit"
      @change="load($event.queryString)"
      :data-qa-id="pendo('searchProduct')"
      class="m-0"
    >
      <template #actions v-if="showShopSwitcher && !showRegionBanner">
        <strong v-if="isMobile">{{ shop.currency }}</strong>
        <strong v-else>{{ trans('Region') }}</strong>
        <MarketSwitcher
          :is-mobile="isMobile"
          :shops="shop.marketRegions"
          :region="shopSelectedRegion"
          :data-qa-id="pendo('switchShop')"
          @change="
            (e) => {
              changeShop(e.country)
              switchSelectedShop()
            }
          "
          @dismiss="
            () => {
              switchSelectedShop()
            }
          "
        />
      </template>
    </Toolbar>

    <Message :message="shop.message" class="mt-6" />

    <div v-if="!loading && categories.length === 0" class="py-8 opaque">
      {{ trans('No products found.') }}
    </div>

    <div v-if="!loading && categories.length > 0" class="grid mx-auto z-1" id="box">
      <div
        v-for="(category, index) in categories"
        :key="category.id"
        :id="'category-' + category.id"
        :ref="(e) => (categoryRefs[index] = e)"
        class="pt-11"
      >
        <h3 v-if="hasMultipleCategories" class="font-bold mb-6">
          {{ category.name.default }}
        </h3>
        <div class="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          <Product
            v-for="product in category.products"
            :key="product.id"
            :id="product.id"
            :product="product"
            :shop="shop"
            :is-mobile="isMobile"
            :is-membership-customer="isMembershipCustomer ?? false"
            :data-qa-id="pendo(`openProductPreview.${product.instanceId}'`)"
            @show="showProductId = product.instanceId"
          />
        </div>
      </div>
    </div>

    <Modal v-if="showProductId" show @hidden="showProductId = undefined">
      <ShopProduct :id="showProductId" :categories="shop.categories" @go-to-category="(e) => goToCategory(e)" />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { pendoLabel as pendo } from '@/munio/utils/index.js'
import { onMounted, onUnmounted, ref, computed, onUpdated, watch } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'

import { trans } from '@/munio/i18n/index.js'
import { ShopCategoryResourceDto, ShopProductResourceDto, ShopResourceDto } from '@/munio/api/data'

import { findIndex, find, maxBy } from 'lodash'

import Toolbar from '@component/Toolbar/index.vue'
import MdlButton from '@component/mdl/Button.vue'
import Product from './Product.vue'
import Message from './Message.vue'
import ShopProduct from '@component/ShopProduct/ShopProduct.vue'
import MarketSwitcher from '@app/Shop/MarketSwitcher.vue'
import MarketSwitcherBanner from '@app/Shop/MarketSwitcherBanner.vue'
import Modal from '@component/Modal.vue'

type Category = ShopCategoryResourceDto & { active: boolean; products: ShopProductResourceDto[] }

const props = defineProps<{
  shop: ShopResourceDto
  isMembershipCustomer: boolean
  products: ShopProductResourceDto[]
  productId?: number
  detectedRegion?: string
  selectedRegion?: string
}>()

const country = window.Munio.country

const categories = ref<Category[]>([])
const loading = ref(false)
const showProductId = ref<number | undefined>()
const shopSelectedRegion = ref<string>(props.shop.country)
const submit = ref(false)
const showRegionBanner = ref(false)
const regionBannerMessage = computed(() =>
  trans("We noticed you're visiting from :region. Choose your country or region to see location-specific content.", {
    region: country(props.detectedRegion ?? props.shop.country)!.i18n,
  }),
)
const container = ref<HTMLDivElement>()
const screenWidth = ref(window.screen.width)
const navBarContainer = ref<HTMLDivElement>()
const navBar = ref<HTMLDivElement>()
const navBarCategoryRefs = ref<HTMLDivElement[]>([])
const isPageScrolledVeritcally = ref(false)
const isNavBarScrollable = ref(false)
const isInPageScroll = ref(true)
const navBarScrollPosition = ref<'left' | 'right' | null>('left')
const isClicked = ref(false)

const categoryRefs = ref<HTMLDivElement[]>([])

const showShopSwitcher = computed(() => {
  return props.shop.marketRegions && props.shop.marketRegions.length > 1
})

const isMobile = computed(() => {
  return screenWidth.value <= 640
})

const activeCategory = computed(() => {
  if (!isInPageScroll.value) {
    return categories.value.length - 1
  }

  return findIndex(categories.value, ['active', true])
})

const hasMultipleCategories = computed(() => {
  return categories.value.length > 1
})

initializeCategories()
assignProductsToCategories(props.products)

function activateCategory(id: number) {
  categories.value.map((category) => (category.active = false))

  let category = find(categories.value, ['id', id])

  if (category) {
    category.active = true
    isClicked.value = true
  }
}

onMounted(async () => {
  window.addEventListener('scroll', handlePageScroll)
  window.addEventListener('resize', handleScreenWidthUpdate)
  navBar.value?.addEventListener('scroll', handleNavBarScroll)

  checkNavBarOverflowing()

  const hash = window.location.hash.substring(1)
  window.scrollTo({ top: document.getElementById(hash)?.offsetTop })

  showProductId.value = find(props.products, (product) => product.id === props.productId)?.instanceId
  submit.value = true

  computeRegion()
})

onUpdated(() => {
  setCategoryIntersectionObserver()
  checkNavBarOverflowing()
  navBar.value?.addEventListener('scroll', handleNavBarScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handlePageScroll)
  window.removeEventListener('resize', handleScreenWidthUpdate)
  navBar.value?.removeEventListener('scroll', handleNavBarScroll)
})

watch(
  () => activeCategory.value,
  (newV, oldV) => {
    if (newV > 0) {
      window.history.replaceState(undefined, '', window.location.search + '#category-' + categories.value[newV]?.id)
    } else {
      window.history.replaceState(undefined, '', window.location.href.substring(0, window.location.href.indexOf('#')))
    }
  },
)

//page navigation
function setCategoryIntersectionObserver() {
  categoryRefs.value.forEach((target: HTMLDivElement, index: number) => {
    useIntersectionObserver(
      target,
      ([{ isIntersecting }]) => {
        if (categories.value[index]) {
          categories.value[index].active = isIntersecting
        }
      },
      {
        root: null,
        threshold: 0.1,
      },
    )
  })
}

function handlePageScroll() {
  if (!container.value || !navBarContainer.value) {
    return
  }

  const offsetTop = container.value.offsetTop + navBarContainer.value?.getBoundingClientRect().top
  isPageScrolledVeritcally.value = window.scrollY > Number(offsetTop) || false

  const verticalScrollePosition = window.scrollY + window.innerHeight + 0.025 * window.innerHeight
  isInPageScroll.value = verticalScrollePosition <= document.body.scrollHeight

  if (navBar.value && navBarContainer.value && !isClicked.value) {
    const scrollInNavBar =
      navBarCategoryRefs.value[activeCategory.value]?.offsetLeft -
      navBarCategoryRefs.value[activeCategory.value - 1]?.getBoundingClientRect().width -
      (navBar.value.offsetLeft - navBarContainer.value.offsetLeft) / 2

    navBar.value.scrollTo({ left: scrollInNavBar, behavior: 'instant' })
  }

  isClicked.value = false
}

function handleNavBarScroll() {
  if (navBar.value?.scrollLeft === 0) {
    navBarScrollPosition.value = 'left'
  } else if (
    navBar.value &&
    Math.round(navBar.value?.scrollLeft + navBar.value?.clientWidth) >= navBar.value?.scrollWidth
  ) {
    navBarScrollPosition.value = 'right'
  } else {
    navBarScrollPosition.value = null
  }
}

function handleScreenWidthUpdate() {
  screenWidth.value = window.screen.width
  checkNavBarOverflowing()
  setCategoryIntersectionObserver()
}

function checkNavBarOverflowing() {
  isNavBarScrollable.value = navBar.value ? navBar.value?.scrollWidth > navBar.value?.clientWidth : false
}

function scrollNavBarLeft() {
  navBar.value?.scrollBy({
    left: 100,
    behavior: 'smooth',
  })
}

function scrollNavBarRight() {
  navBar.value?.scrollBy({
    left: -100,
    behavior: 'smooth',
  })
}

function goToCategory(e: number) {
  showProductId.value = undefined
  scrollToCategoryInPage(e)
}

function scrollToCategoryInPage(categoryId: number) {
  const element = document.getElementById('category-' + categoryId)

  if (element && navBarContainer.value) {
    window.scrollTo({
      top: document.getElementById('category-' + categoryId)?.offsetTop,
    })
  }
}

// data loading
function initializeCategories() {
  categories.value.splice(0, categories.value.length)

  const maxSortOrder = maxBy(props.shop.categories, 'sortOrder')?.sortOrder ?? 0

  categories.value.push(
    ...[
      ...(props.shop.categories?.map((category) => ({
        id: category.id,
        name: { default: category.name.default },
        url: category?.url,
        sortOrder: category.sortOrder,
        products: [],
        active: false,
      })) || []),
      {
        id: -1,
        name: { default: trans('Uncategorized') },
        sortOrder: maxSortOrder + 1,
        url: null,
        products: [],
        active: false,
      },
    ],
  )
}

function assignProductsToCategories(products: ShopProductResourceDto[]) {
  products.forEach((product: ShopProductResourceDto) => {
    if (product.categories.length === 0) {
      categories.value[categories.value.length - 1].products.push(product)
    } else {
      product.categories.forEach((categoryId: number) => {
        const category = categories.value.find((cat) => cat.id === categoryId)

        if (category) {
          category.products.push(product)
        }
      })
    }
  })

  const filteredCategories = categories.value.filter((category) => category.products.length > 0)

  categories.value.splice(0, categories.value.length)
  categories.value.push(...filteredCategories)
}

async function load(options?: URLSearchParams) {
  try {
    loading.value = true

    const { data } = await window.Munio.api.shop().products(options)

    initializeCategories()
    assignProductsToCategories(data.data)

    window.history.pushState({}, '', window.location.pathname + window.location.search)
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

function changeShop(region: string) {
  shopSelectedRegion.value = region
}

function computeRegion() {
  // you've made your choice
  if (props.selectedRegion) {
    return
  }

  if (
    // we have detected a region
    props.detectedRegion &&
    // and the detected region is not the same as the shop region
    props.detectedRegion !== props.shop.country
  ) {
    showRegionBanner.value = true
  }
}

async function dismissShop() {
  const country = props.shop.country
  await window.Munio.api.shop().region(country)
  showRegionBanner.value = false
}

async function switchSelectedShop() {
  await window.Munio.api.shop().region(shopSelectedRegion.value)
  showRegionBanner.value = false
}
</script>
